import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/contexts/CartContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ShoppingCart, Settings, ArrowLeft, Star, Shield, Clock } from "lucide-react";
import HTMLContent from "@/components/HTMLContent";

interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  type: 'product' | 'service';
  category_name?: string;
  billing_type?: 'monthly' | 'quarterly' | 'biannual' | 'annual' | 'one_time';
}

const ServiceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedServices, setRelatedServices] = useState<Service[]>([]);
  const { addToCart } = useCart();

  useEffect(() => {
    if (id) {
      fetchService();
    }
  }, [id]);

  const fetchService = async () => {
    try {
      const { data, error } = await supabase
        .from('products_2025_11_10_14_11')
        .select(`
          id,
          name,
          description,
          price,
          image_url,
          type,
          billing_type,
          categories_2025_11_10_14_11(name)
        `)
        .eq('id', id)
        .eq('type', 'service')
        .eq('is_active', true)
        .single();

      if (error) throw error;

      const formattedService = {
        ...data,
        category_name: data.categories_2025_11_10_14_11?.name
      };

      setService(formattedService);

      // Fetch related services
      if (data.categories_2025_11_10_14_11) {
        fetchRelatedServices(data.category_id, data.id);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudo cargar el servicio",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedServices = async (categoryId: string, currentServiceId: string) => {
    try {
      const { data, error } = await supabase
        .from('products_2025_11_10_14_11')
        .select(`
          id,
          name,
          description,
          price,
          image_url,
          type,
          billing_type,
          categories_2025_11_10_14_11(name)
        `)
        .eq('category_id', categoryId)
        .eq('type', 'service')
        .eq('is_active', true)
        .neq('id', currentServiceId)
        .limit(3);

      if (error) throw error;

      const formattedServices = data?.map(service => ({
        ...service,
        category_name: service.categories_2025_11_10_14_11?.name
      })) || [];

      setRelatedServices(formattedServices);
    } catch (error: any) {
      console.error('Error fetching related services:', error);
    }
  };

  const handleAddToCart = (service: Service) => {
    addToCart({
      id: service.id,
      name: service.name,
      price: service.price,
      type: service.type,
      image_url: service.image_url
    });
    toast({
      title: "Servicio agregado",
      description: `${service.name} se agregó al carrito`,
    });
  };

  const getBillingTypeLabel = (billingType?: string) => {
    const labels = {
      'monthly': 'Mensual',
      'quarterly': 'Trimestral',
      'biannual': 'Semestral',
      'annual': 'Anual',
      'one_time': 'Pago único'
    };
    return labels[billingType as keyof typeof labels] || 'Pago único';
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="h-96 bg-muted rounded-lg"></div>
            <div className="space-y-4">
              <div className="h-8 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
              <div className="h-20 bg-muted rounded"></div>
              <div className="h-12 bg-muted rounded w-1/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <Settings className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Servicio no encontrado</h1>
          <p className="text-muted-foreground mb-8">
            El servicio que buscas no existe o no está disponible.
          </p>
          <Link to="/servicios">
            <Button>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Servicios
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Link to="/" className="hover:text-primary">Inicio</Link>
        <span>/</span>
        <Link to="/servicios" className="hover:text-primary">Servicios</Link>
        <span>/</span>
        <span className="text-foreground">{service.name}</span>
      </div>

      {/* Service Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Service Image */}
        <div className="space-y-4">
          <div className="aspect-square rounded-lg overflow-hidden bg-muted">
            {service.image_url ? (
              <img 
                src={service.image_url} 
                alt={service.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Settings className="h-24 w-24 text-muted-foreground" />
              </div>
            )}
          </div>
        </div>

        {/* Service Info */}
        <div className="space-y-6">
          {service.category_name && (
            <Badge variant="secondary" className="w-fit">
              {service.category_name}
            </Badge>
          )}
          
          <div>
            <h1 className="text-3xl font-bold mb-2">{service.name}</h1>
            <div className="flex items-center gap-4 mb-4">
              <span className="text-3xl font-bold text-primary">
                ${service.price.toFixed(2)}
              </span>
              {service.billing_type && (
                <Badge variant="outline">
                  {getBillingTypeLabel(service.billing_type)}
                </Badge>
              )}
            </div>
          </div>

          {/* Service Type */}
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-blue-600 border-blue-600">
              ✓ Servicio Profesional
            </Badge>
          </div>

          {/* Description */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Descripción del Servicio</h3>
            <HTMLContent 
              content={service.description} 
              className="text-muted-foreground leading-relaxed"
            />
          </div>

          {/* Service Features */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Star className="h-5 w-5 text-yellow-500" />
              <span className="text-sm font-medium">Calidad Premium</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Shield className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium">Soporte 24/7</span>
            </div>
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
              <Clock className="h-5 w-5 text-blue-500" />
              <span className="text-sm font-medium">Entrega Rápida</span>
            </div>
          </div>

          {/* What's Included */}
          <div className="bg-muted/30 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">¿Qué incluye este servicio?</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>✓ Consultoría inicial gratuita</li>
              <li>✓ Configuración completa del servicio</li>
              <li>✓ Soporte técnico especializado</li>
              <li>✓ Garantía de satisfacción</li>
              <li>✓ Mantenimiento incluido</li>
            </ul>
          </div>

          {/* Add to Cart */}
          <div className="space-y-4">
            <Button 
              size="lg"
              className="w-full"
              onClick={() => handleAddToCart(service)}
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              Contratar Servicio
            </Button>
            
            <p className="text-sm text-muted-foreground text-center">
              Te contactaremos en las próximas 24 horas para coordinar el servicio
            </p>
          </div>
        </div>
      </div>

      {/* Process Steps */}
      <div className="mb-12">
        <Separator className="mb-8" />
        <h2 className="text-2xl font-bold mb-6 text-center">¿Cómo funciona?</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
              1
            </div>
            <h3 className="font-semibold mb-2">Contrata</h3>
            <p className="text-sm text-muted-foreground">Agrega el servicio al carrito y procede al pago</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
              2
            </div>
            <h3 className="font-semibold mb-2">Contacto</h3>
            <p className="text-sm text-muted-foreground">Te contactamos para entender tus necesidades</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
              3
            </div>
            <h3 className="font-semibold mb-2">Desarrollo</h3>
            <p className="text-sm text-muted-foreground">Implementamos la solución personalizada</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
              4
            </div>
            <h3 className="font-semibold mb-2">Entrega</h3>
            <p className="text-sm text-muted-foreground">Entregamos el servicio y brindamos soporte</p>
          </div>
        </div>
      </div>

      {/* Related Services */}
      {relatedServices.length > 0 && (
        <div>
          <Separator className="mb-8" />
          <h2 className="text-2xl font-bold mb-6">Servicios Relacionados</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedServices.map((relatedService) => (
              <Card key={relatedService.id} className="group hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="h-48 rounded-lg overflow-hidden mb-4 bg-muted">
                    {relatedService.image_url ? (
                      <img 
                        src={relatedService.image_url} 
                        alt={relatedService.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Settings className="h-16 w-16 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <CardTitle className="text-lg line-clamp-2">{relatedService.name}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    <HTMLContent content={relatedService.description} />
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-xl font-bold text-primary">
                      ${relatedService.price.toFixed(2)}
                    </span>
                    {relatedService.billing_type && (
                      <Badge variant="outline" className="text-xs">
                        {getBillingTypeLabel(relatedService.billing_type)}
                      </Badge>
                    )}
                  </div>
                  <Link to={`/servicio/${relatedService.id}`}>
                    <Button variant="outline" className="w-full">
                      Ver Detalles
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetail;